import { useQuery } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Api from '../../../../api/Api';
import { CreateReportData } from '../../../../api/endpoints/Service';
import { QUERY_KEYS } from '../../../../queries/queryKeys';
import useCreateReportMutation from '../../../../queries/useCreateReportMutation';
import useAuthStore from '../../../../store/authStore';
import { IReports } from '../../../../types/reports';
import {
  SubscriptionType,
  WaterdataGroup,
} from '../../../../types/userGroupPermissions';
import { userGroupValidation } from '../../../../utils/userGroupValidation';

export const REQUEST_REPORT_STATUS = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  PENDING: 'PENDING',
} as const;

export const useOndemandParams = (params: IReports) => {
  const {
    datasets,
    userInput,
    updateUserInput,
    defaultDataset,
    pointName,
    location,
  } = params;

  const [datasetSW, setDatasetSW] = useState<string>('');
  const [datasetHD, setDatasetHD] = useState<string>('');
  const [datasetWind, setDatasetWind] = useState<string>('');
  const [timeStep, setTimeStep] = useState<string>('60');
  const [startDate, setStartDate] = useState(userInput.startDate || null);
  const [endDate, setEndDate] = useState(userInput.endDate || null);

  const user = useAuthStore((state) => state.user);

  const metadataQuerySW = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetSW],
    queryFn: async () =>
      datasetSW ? await Api.metadata.getMetadata(datasetSW) : null,
    enabled: !!datasetSW,
  });

  const metadataQueryHD = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetHD],
    queryFn: async () =>
      datasetHD ? await Api.metadata.getMetadata(datasetHD) : null,
    enabled: !!datasetHD,
  });

  const metadataQueryWind = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetWind],
    queryFn: async () =>
      datasetWind ? await Api.metadata.getMetadata(datasetWind) : null,
    enabled: !!datasetWind,
  });

  const { mutate, isPending, isError, isSuccess } = useCreateReportMutation({});

  const getValidatedRange = useCallback(
    (metadata: any) => {
      if (!metadata?.TimeDefinition || !user) return null;
      return userGroupValidation(
        user?.waterdataGroup
          ? WaterdataGroup[user.waterdataGroup as keyof typeof WaterdataGroup]
          : null,
        user?.subscriptionType
          ? SubscriptionType[
              user.subscriptionType as keyof typeof SubscriptionType
            ]
          : null,
        metadata.TimeDefinition,
        user?.confidentialDatasetIds || []
      ).dateRange;
    },
    [user]
  );

  const validatedDateRangeSW = useMemo(
    () => getValidatedRange(metadataQuerySW.data),
    [metadataQuerySW.data, getValidatedRange]
  );
  const validatedDateRangeHD = useMemo(
    () => getValidatedRange(metadataQueryHD.data),
    [metadataQueryHD.data, getValidatedRange]
  );
  const validatedDateRangeWind = useMemo(
    () => getValidatedRange(metadataQueryWind.data),
    [metadataQueryWind.data, getValidatedRange]
  );

  const dateRangeFromMetadata = useMemo(() => {
    const validatedRanges = [
      validatedDateRangeSW,
      validatedDateRangeHD,
      validatedDateRangeWind,
    ].filter(Boolean) as [Moment, Moment][];
    if (validatedRanges.length === 0) return null;

    const calculatedStartDate = moment.max(validatedRanges.map((r) => r[0]));
    const calculatedEndDate = moment.min(validatedRanges.map((r) => r[1]));

    if (calculatedStartDate.isAfter(calculatedEndDate)) {
      console.warn('No date overlap between datasets after validation');
      return null;
    }
    return [calculatedStartDate, calculatedEndDate] as [Moment, Moment];
  }, [validatedDateRangeSW, validatedDateRangeHD, validatedDateRangeWind]);

  const doStartDateChange = useCallback(
    (value: any) => value && setStartDate(value),
    []
  );
  const doEndDateChange = useCallback(
    (value: any) => value && setEndDate(value),
    []
  );

  const disabledStartDate = useCallback(
    (current: Moment) => {
      if (!dateRangeFromMetadata || !endDate) return false;
      return (
        (current && current > endDate) ||
        current.isBefore(dateRangeFromMetadata[0], 'day')
      );
    },
    [endDate, dateRangeFromMetadata]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      if (!dateRangeFromMetadata || !startDate) return false;
      return (
        (current && current < startDate) ||
        current.isAfter(dateRangeFromMetadata[1], 'day')
      );
    },
    [startDate, dateRangeFromMetadata]
  );

  const handleDatasetChange = useCallback(
    (type: 'SW' | 'HD' | 'Wind') => (value: string) => {
      const actualValue = value === 'none' ? '' : value;
      if (type === 'SW') setDatasetSW(actualValue);
      if (type === 'HD') setDatasetHD(actualValue);
      if (type === 'Wind') setDatasetWind(actualValue);
    },
    []
  );

  const getDefaultValue = useCallback(
    (key: string) => {
      return defaultDataset.find((item) => item.key === key)?.value || '';
    },
    [defaultDataset]
  );

  const getLocationString = (location: number[]) => {
    const [lat, lng] = location;
    return `${lat}, ${lng}`;
  };

  const handleUpdate = useCallback(() => {
    if (!startDate || !endDate) {
      return;
    }
    const payload: CreateReportData = {
      pointName,
      datasetIds: [datasetSW, datasetHD, datasetWind],
      timestepInterval: Number(timeStep),
      startTime: moment(startDate).format('YYYY-MM-DD'),
      endTime: moment(endDate).format('YYYY-MM-DD'),
      location: getLocationString(location),
    };

    mutate(payload);
  }, [
    datasetSW,
    datasetHD,
    datasetWind,
    timeStep,
    startDate,
    endDate,
    location,
  ]);

  useEffect(() => {
    if (defaultDataset.length > 0 && !datasetSW && !datasetHD && !datasetWind) {
      setDatasetSW(userInput.datasetSW || getDefaultValue('SW'));
      setDatasetHD(userInput.datasetHD || getDefaultValue('HD'));
      setDatasetWind(userInput.datasetWind || getDefaultValue('Wind'));
    }
  }, [
    defaultDataset,
    userInput,
    datasetSW,
    datasetHD,
    datasetWind,
    getDefaultValue,
  ]);

  useEffect(() => {
    updateUserInput({
      datasetSW,
      datasetHD,
      datasetWind,
      timeStep,
      startDate,
      endDate,
    });
  }, [
    startDate,
    endDate,
    timeStep,
    datasetSW,
    datasetHD,
    datasetWind,
    updateUserInput,
  ]);

  const getRequestReportStatus = () => {
    if (isError) {
      return REQUEST_REPORT_STATUS.ERROR;
    } else if (isPending) {
      return REQUEST_REPORT_STATUS.PENDING;
    } else if (isSuccess) {
      return REQUEST_REPORT_STATUS.SUCCESS;
    }
  };

  useEffect(() => {
    if (dateRangeFromMetadata) {
      const [validMin, validMax] = dateRangeFromMetadata;

      setStartDate((currentStart) => {
        if (
          !currentStart ||
          currentStart.isBefore(validMin) ||
          currentStart.isAfter(validMax)
        ) {
          return validMin;
        }
        return currentStart;
      });

      setEndDate((currentEnd) => {
        if (
          !currentEnd ||
          currentEnd.isAfter(validMax) ||
          currentEnd.isBefore(validMin)
        ) {
          return validMax;
        }
        return currentEnd;
      });
    }
  }, [dateRangeFromMetadata]);

  return {
    datasetSW,
    datasetHD,
    datasetWind,
    timeStep,
    startDate,
    endDate,
    datasets,
    requestReportStatus: getRequestReportStatus(),

    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    handleDatasetChange,
    handleUpdate,
  };
};
